"use client";
import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

// local component imports
import ReelItem from "./ReelItem";
import { getIconUrl } from "../../../../../../../utils/common";

/**
 * MobileFullScreen renders a fullscreen vertical reel carousel as a portal.
 * @param {object} props - Component props
 * @param {Array} props.videos - Array of video objects to display
 * @param {number} props.initialIndex - Index of the first video to show
 * @param {Function} props.onClose - Callback to close the fullscreen view
 * @returns {React.ReactElement} Portal-rendered fullscreen reel container
 */
const MobileFullScreen = ({ videos, initialIndex, onClose, videoProducts = [] }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isMuted, setIsMuted] = useState(true);
  const [showScrollHint, setShowScrollHint] = useState(true);
  /**
   * Toggles the muted state for all reels.
   */
  const onToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  // Intersection Observer for detecting active video
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const index = Number(entry.target.getAttribute("data-index"));
            if (!isNaN(index)) {
              setActiveIndex(index);
            }
          }
        });
      },
      {
        root: container,
        threshold: 0.6,
      },
    );

    const items = container.querySelectorAll("[data-index]");
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [videos]);

  // Lock body scroll when fullscreen is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Hide scroll hint after 2.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowScrollHint(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to initial video
  useEffect(() => {
    if (containerRef.current) {
      const targetScroll = initialIndex * window.innerHeight;
      containerRef.current.scrollTop = targetScroll;
    }
  }, [initialIndex]);

  return createPortal(
    <div className="bg-black fixed inset-0 z-[9999]">
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-12 z-[10000] flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[rgba(0,0,0,0.20)]"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Swipe hint overlay — shown for 4 seconds on first open */}
      {showScrollHint && (
        <div
          className="pointer-events-none absolute inset-0 z-[10001] flex flex-col items-center justify-center transition-opacity duration-500"
          style={{ background: "rgba(0, 0, 0, 0.80)" }}
        >
          <img
            src={`${getIconUrl("/icons/swipe_up.svg")}`}
            alt="Swipe up to see more videos"
            className="h-[120px] w-[120px] animate-bounce"
            draggable={false}
          />
          <p className="mt-4 text-sm font-medium text-[#ffffff] opacity-90">Swipe up for more</p>
        </div>
      )}

      {/* Scrollable reel container */}
      <div ref={containerRef} className="h-full w-full snap-y snap-mandatory overflow-y-scroll overscroll-contain">
        {videos.map((video, index) => (
          <div
            key={video.id || `video-${index}`}
            data-index={index}
            className="border-b-4 border-[#202020] bg-[#111111]"
          >
            <ReelItem
              video={video}
              isActive={index === activeIndex}
              isMuted={isMuted}
              onToggleMute={onToggleMute}
              products={videoProducts?.[index] ?? []}
            />
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
};

MobileFullScreen.propTypes = {
  videos: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialIndex: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  videoProducts: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),
};

export default MobileFullScreen;
